/**
 * Utilitário para gerenciar atualizações do Service Worker (PWA).
 * O registro do SW deve ser feito apenas em produção (chamada já condicionada em index.js).
 */

import { APP_PWA_UPDATE_EVENT, emitRuntimeEvent } from './runtimeEvents';

let registration = null;
let updateAvailable = false;

/**
 * Registrar Service Worker e configurar listeners.
 * Chamar apenas em produção para evitar cache em desenvolvimento.
 */
export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
        .then((reg) => {
          registration = reg;
          console.log('[SW] Service Worker registrado:', reg.scope);

          if (reg.waiting) {
            updateAvailable = true;
            notifyUpdateAvailable();
          }

          // Verificar atualizações periodicamente
          setInterval(() => {
            reg.update();
          }, 60 * 60 * 1000); // A cada hora

          // Listener para novo Service Worker instalado
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (!newWorker) return;

            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Novo SW instalado, notificar usuário
                updateAvailable = true;
                notifyUpdateAvailable();
              }
            });
          });

          // Listener para mensagens do SW
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
              updateAvailable = true;
              notifyUpdateAvailable();
            }
          });
        })
        .catch((error) => {
          console.error('[SW] Erro ao registrar Service Worker:', error);
        });
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (updateAvailable) {
      window.location.reload();
    }
  });
};

/**
 * Notificar usuário sobre atualização disponível
 */
const notifyUpdateAvailable = () => {
  emitRuntimeEvent(APP_PWA_UPDATE_EVENT, {
    message: 'Nova versão do SisGEM disponível.'
  });
};

/**
 * Forçar atualização do Service Worker
 */
export const forceUpdate = () => {
  if (registration) {
    registration.update().then(() => {
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    });
  }
};

/**
 * Limpar todos os caches
 */
export const clearAllCaches = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.serviceWorker.controller) {
      reject(new Error('Service Worker não está ativo'));
      return;
    }

    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      if (event.data.success) {
        resolve();
      } else {
        reject(new Error(event.data.error || 'Erro ao limpar caches'));
      }
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'CLEAR_CACHE' },
      [channel.port2]
    );
  });
};

/**
 * Verificar status offline/online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Listener para mudanças de status online/offline
 */
export const onOnlineStatusChange = (callback) => {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
};
